import { CommonModule } from '@angular/common';
import { Component, OnInit, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastService } from 'angular-toastify';
import { ParkingShiftTableRow } from '../../../../core/models/finance/parking-shift.model';
import { MapperParkingShiftService } from '../../../../core/mappers/finance/mapper-parking-shift.service';
import { ApiParkingShiftService } from '../../../../core/services/api/finance/api-parking-shift.service';
import { SiteStateService } from '../../../../core/services/site-state.service';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ModalParkingShiftDetailComponent } from '../../modals/finance/modal-parking-shift-detail/modal-parking-shift-detail.component';

@Component({
  selector: 'app-parking-shift',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalParkingShiftDetailComponent],
  templateUrl: './parking-shift.component.html',
  styleUrl: './parking-shift.component.css',
})
export class ParkingShiftComponent implements OnInit {
  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiParkingShiftService);
  private readonly mapper = inject(MapperParkingShiftService);
  private readonly toast = inject(ToastService);

  items = signal<ParkingShiftTableRow[]>([]);
  isLoading = signal(false);
  isModalOpen = signal(false);
  selectedId = signal(0);

  statusFilter: '' | 'open' | 'closed' = '';
  openDateFrom = this.todayInputValue();
  openDateTo = this.todayInputValue();

  currentPage = signal(1);
  pageSize = signal(20);
  pageSizeOptions: number[] = [10, 20, 50, 100];
  totalItems = signal(0);
  totalPages = signal(1);

  constructor() {
    effect(() => {
      const siteId = this.siteState.selectedSite()?.id;
      untracked(() => {
        this.currentPage.set(1);
        if (siteId) this.loadTable();
        else this.items.set([]);
      });
    });
  }

  /** yyyy-MM-dd for <input type="date"> (local calendar day) */
  private todayInputValue(): string {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  /** Dates are required — keep both filled with today if cleared. */
  private ensureDateFilters(): void {
    const today = this.todayInputValue();
    if (!this.openDateFrom?.trim()) this.openDateFrom = today;
    if (!this.openDateTo?.trim()) this.openDateTo = today;
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get startItemIndex(): number {
    if (this.totalItems() === 0) return 0;
    return (this.currentPage() - 1) * this.pageSize() + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage() * this.pageSize(), this.totalItems());
  }

  get pagesArray(): number[] {
    const maxVisibleButtons = 5;
    let startPage = Math.max(1, this.currentPage() - 2);
    let endPage = Math.min(this.totalPages(), startPage + maxVisibleButtons - 1);
    if (endPage - startPage < maxVisibleButtons - 1) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }

  ngOnInit(): void {
    if (this.selectedSiteId) this.loadTable();
  }

  onFilterChange(): void {
    this.ensureDateFilters();
    this.currentPage.set(1);
    this.loadTable();
  }

  onRefresh(): void {
    this.currentPage.set(1);
    this.loadTable();
  }

  openDetail(item: ParkingShiftTableRow): void {
    this.selectedId.set(item.id);
    this.isModalOpen.set(true);
  }

  closeModal(): void {
    this.isModalOpen.set(false);
    this.selectedId.set(0);
  }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
      this.loadTable();
    }
  }

  onPageSizeChange(event: Event): void {
    const element = event.target as HTMLSelectElement;
    this.pageSize.set(Number(element.value));
    this.currentPage.set(1);
    this.loadTable();
  }

  trackById(_: number, item: ParkingShiftTableRow): number {
    return item.id;
  }

  formatMoney(value: number | null | undefined): string {
    if (value == null || Number.isNaN(Number(value))) return '—';
    return Number(value).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  loadTable(): void {
    const siteId = this.selectedSiteId;
    if (!siteId) {
      this.items.set([]);
      return;
    }

    this.ensureDateFilters();

    this.isLoading.set(true);
    this.api
      .getParkingShiftsForTable({
        siteId,
        page: this.currentPage(),
        pageSize: this.pageSize(),
        openDateFrom: this.openDateFrom,
        openDateTo: this.openDateTo,
        status: this.statusFilter || undefined,
      })
      .subscribe({
        next: (res) => {
          const mapped = this.mapper.mapTable(res);
          this.items.set(mapped.items);
          this.totalItems.set(mapped.total);
          this.totalPages.set(Math.max(1, mapped.totalPages));
          this.isLoading.set(false);
        },
        error: () => {
          this.items.set([]);
          this.isLoading.set(false);
          this.toast.error('โหลดข้อมูลกะล้มเหลว');
        },
      });
  }
}
